import { useCallback, useState } from 'react';

import { DropdownType } from '../store/structureDefinitions/modalState';
import useAsyncAbortableDispatcher from './useAsyncAbortableDispatcher';
import useModal from './useModal';

const allowedWords = [
    'öl',
    'te',
    'sj',
    'is',
    'lm',
    'wc',
    'ld',
    'aa',
    'ob',
    'gb',
    'a4',
    'dr',
    'v6',
    'hp',
    '9v',
    'up',
    'pg',
    'vb',
    'a5',
    'mr',
    'tv',
    'yo',
    'a6',
    '20',
    'c4',
    'c5',
];

const useAbortableDebouncedSearch = <TResult>(
    inputText: string,
    searchFunction: (
        signal: AbortSignal,
        query: string,
        searchType: 'default' | 'direct',
    ) => Promise<TResult>,
    minimumQueryLength: number,
    searchType: 'default' | 'direct',
    delay: number,
) => {
    const search = async (
        abortSignal: AbortSignal,
        text: string,
        _searchType: 'default' | 'direct',
    ) => {
        const normalizedText = text.trim().toLocaleLowerCase();

        if (normalizedText.length === 0) {
            return undefined;
        }
        const isWordAllowed = allowedWords.some((word) => word.startsWith(normalizedText));

        if (normalizedText.length < minimumQueryLength && !isWordAllowed) {
            return undefined;
        }

        return searchFunction(abortSignal, text, _searchType);
    };

    const { status, error, result, isLoading } = useAsyncAbortableDispatcher(
        search,
        [inputText, searchType],
        {
            executeOnMount: !!inputText,
            keepPreviousResult: true,
            debounced: true,
            debounceTime: delay,
        },
    );

    const hasResponse = !!(status === 'success' && !!result) || !!(status === 'error' && error);

    return {
        hasResponse,
        result,
        error,
        isLoading,
        status,
    };
};

export const useSearchDropdownState = <TResult>(
    initialInputText: string | null,
    searchFunction: (
        signal: AbortSignal,
        query: string,
        searchType: 'default' | 'direct',
    ) => Promise<TResult>,
    minimumQueryLength: number,
    initialSearchType: 'default' | 'direct',
    debounceTime?: number,
) => {
    const { open, close, isOpen } = useModal(DropdownType.Search);

    const [query, setQuery] = useState(initialInputText || '');
    const [searchType, setSearchType] = useState(initialSearchType);
    const [a11yResultAnnounceMessage, setA11yResultAnnounceMessage] = useState('');

    const delay = debounceTime || 300;

    const { hasResponse, result, error, status, isLoading } = useAbortableDebouncedSearch(
        query,
        searchFunction,
        minimumQueryLength,
        searchType,
        delay,
    );

    const resetQuery = useCallback(() => setQuery(''), []);

    const clear = useCallback(() => {
        close();
        resetQuery();
        setA11yResultAnnounceMessage('');
    }, [close, resetQuery]);

    return {
        result,
        isOpen,
        clear,
        open,
        close,
        error,
        show: hasResponse,
        hasResponse,
        query,
        setQuery,
        setSearchType,
        resetQuery,
        status,
        isLoading,
        resultDescription: a11yResultAnnounceMessage,
        setA11yResultAnnounceMessage,
    };
};
